import React, { useEffect, useState } from "react";
import axios from "axios";

interface ShowcaseProfile {
  _id: string;
  profilePhoto?: string;
  displayName?: string;
}

interface Props {
  message?: string;
}

export const MaintenancePage: React.FC<Props> = ({ message }) => {
  const [photos, setPhotos] = useState<string[]>([]);

  useEffect(() => {
    const BASE = process.env.API_BASE_URL || "https://api.crays.co.za/app";
    axios
      .get(`${BASE}/v1/profiles/showcase`)
      .then((res) => {
        const list: ShowcaseProfile[] = res.data?.data ?? [];
        const urls = list
          .map((p) => p.profilePhoto)
          .filter(Boolean) as string[];
        setPhotos(urls);
      })
      .catch(() => {});
  }, []);

  // Duplicate for seamless loop
  const strip = photos.length ? [...photos, ...photos] : [];

  return (
    <div className="min-h-screen bg-charcoal flex flex-col items-center justify-center p-6 text-center font-body overflow-hidden relative">
      {/* Sliding photo background */}
      {strip.length > 0 && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Overlay */}
          <div className="absolute inset-0 bg-charcoal/80 z-10" />

          {/* Two rows scrolling in opposite directions */}
          <div className="absolute top-0 left-0 right-0 h-1/2 flex items-end pb-1">
            <div
              className="flex gap-2 animate-slide-left"
              style={{ width: "max-content" }}
            >
              {strip.map((src, i) => (
                <div
                  key={i}
                  className="w-40 h-40 flex-shrink-0 rounded-xl overflow-hidden opacity-80"
                >
                  <img
                    src={src}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1/2 flex items-start pt-1">
            <div
              className="flex gap-2 animate-slide-right"
              style={{ width: "max-content" }}
            >
              {[...strip].reverse().map((src, i) => (
                <div
                  key={i}
                  className="w-40 h-40 flex-shrink-0 rounded-xl overflow-hidden opacity-80"
                >
                  <img
                    src={src}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Card */}
      <div className="relative z-20 bg-charcoal/70 backdrop-blur-xl border border-white/10 rounded-3xl px-8 py-10 max-w-sm w-full shadow-2xl">
        <div className="w-16 h-16 rounded-2xl bg-yellow/10 border border-yellow/20 flex items-center justify-center mx-auto mb-5">
          <svg
            className="w-8 h-8 text-yellow"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437 1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008Z"
            />
          </svg>
        </div>
        <h1 className="font-display font-bold text-2xl text-offwhite mb-2">
          We'll be back soon
        </h1>
        <p className="text-offwhite/50 text-sm leading-relaxed">
          {message ||
            "Crays is currently undergoing maintenance. Please check back shortly."}
        </p>
        <div className="mt-6 flex items-center justify-center gap-1.5">
          <span
            className="w-1.5 h-1.5 bg-yellow rounded-lg animate-bounce"
            style={{ animationDelay: "0ms" }}
          />
          <span
            className="w-1.5 h-1.5 bg-yellow rounded-lg animate-bounce"
            style={{ animationDelay: "150ms" }}
          />
          <span
            className="w-1.5 h-1.5 bg-yellow rounded-lg animate-bounce"
            style={{ animationDelay: "300ms" }}
          />
        </div>
      </div>

      <style>{`
        @keyframes slideLeft {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @keyframes slideRight {
          from { transform: translateX(-50%); }
          to { transform: translateX(0); }
        }
        .animate-slide-left { animation: slideLeft 30s linear infinite; }
        .animate-slide-right { animation: slideRight 30s linear infinite; }
      `}</style>
    </div>
  );
};
